import { AkamaiAccountProvider } from '@bnnvara/accounts';
import { AtInternet } from '@bnnvara/analytics/src/components/AtInternet/AtInternet';
import { CookieConsentModule } from '@bnnvara/ccm';
import { FacebookProvider } from '@bnnvara/embeds';
import { GoogleOptimizeProvider } from '@bnnvara/google-optimize';
import { SterBannerProvider } from '@bnnvara/ster';
import theme, { Fonts } from '@bnnvara/themes';
import App from 'next/app';
import getConfig from 'next/config';
import Head from 'next/head';
import Router from 'next/router';
import React from 'react';
import { ThemeProvider } from 'styled-components';

import Account from '../components/Account';
import { BookmarkProvider } from '../components/Bookmark/BookmarkProvider';
import { FollowBrandProvider } from '../components/FollowBrand/FollowBrandProvider';
import { NotificationsProvider } from '../hooks/notifications';
import { RefContextProvider } from '../hooks/useSharedRef';
import { UIContextProvider } from '../hooks/useUI';
import { GlobalStyle } from '../layouts/styled';

const { publicRuntimeConfig } = getConfig();

class Application extends App {
  state = {
    brand: null,
  };

  render() {
    const { Component, pageProps, err } = this.props;
    const { brand } = this.state;

    // Workaround for https://github.com/zeit/next.js/issues/8592
    const modifiedPageProps = { ...pageProps, err };

    let content = (
      <Component
        {...modifiedPageProps}
        onBrandLoaded={(loadedBrand) => this.setState({ brand: loadedBrand })}
      />
    );

    if (brand) {
      content = (
        <AkamaiAccountProvider
          config={{
            avatarEndpoint: publicRuntimeConfig.akamai.avatarEndpoint,
            hostedLoginEndpoint: publicRuntimeConfig.akamai.endpoint,
            clientId: publicRuntimeConfig.akamai.clientId,
            redirectUri: publicRuntimeConfig.akamai.redirectUri,
            postLogoutRedirectUri:
              publicRuntimeConfig.akamai.postLogoutRedirectUri,
            popupRedirectUri: publicRuntimeConfig.akamai.popupRedirectUri,
            silentRedirectUri: publicRuntimeConfig.akamai.silentRedirectUri,
          }}
        >
          <Account>
            <FollowBrandProvider>
              <BookmarkProvider>
                <NotificationsProvider>{content}</NotificationsProvider>
              </BookmarkProvider>
            </FollowBrandProvider>
          </Account>
          <AtInternet
            id={publicRuntimeConfig.atInternet.id}
            routerEvents={Router.events}
            siteInfo={{
              chapter1: brand.slug,
              level2: brand.atInternetId,
            }}
          />
        </AkamaiAccountProvider>
      );
    }

    return (
      <React.Fragment>
        <Head>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=2"
          />
          {process.env.NODE_ENV !== 'production' && (
            <>
              <meta name="robots" content="noindex nofollow" />
              <meta
                key="googlebot"
                name="googlebot"
                content="noindex,nofollow"
              />
            </>
          )}
          {process.env.NODE_ENV === 'production' && (
            <>
              <CookieConsentModule
                organisation={publicRuntimeConfig.ccm.organisation}
                site={publicRuntimeConfig.ccm.site}
                ccmDomain={publicRuntimeConfig.ccm.domain}
                ccmWrapperVersion={publicRuntimeConfig.ccm.wrapperVersion}
              />
              <meta
                httpEquiv="Content-Security-Policy"
                content="upgrade-insecure-requests"
              />
            </>
          )}
          <script src="/js/oidc-client.min.js" />
          <script
            dangerouslySetInnerHTML={{ // eslint-disable-line
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${publicRuntimeConfig.gtm.id}');`,
            }}
          />

          <link
            rel="apple-touch-icon"
            sizes="180x180"
            href="/static/favicon/apple-touch-icon.png"
          />

          <link
            rel="icon"
            type="image/png"
            sizes="32x32"
            className="js-favicon"
            href="/static/favicon/favicon-32x32.png"
          />

          <link
            rel="icon"
            type="image/png"
            sizes="16x16"
            href="/static/favicon/favicon-16x16.png"
          />

          <link rel="manifest" href="/static/favicon/site.webmanifest" />

          <link
            rel="mask-icon"
            href="/static/favicon/safari-pinned-tab.svg"
            color={theme.bnnvara.primary.main}
          />

          <meta
            name="msapplication-TileImage"
            content="/static/favicon/msapplication-TileImage-270x270.jpg"
          />

          <meta name="theme-color" content={theme.bnnvara.base.black} />
        </Head>
        <ThemeProvider theme={theme.bnnvara}>
          <Fonts />
          <GlobalStyle />
          <GoogleOptimizeProvider>
            <FacebookProvider appId={publicRuntimeConfig.facebook.appId} wait>
              <SterBannerProvider>
                <RefContextProvider>
                  <UIContextProvider>{content}</UIContextProvider>
                </RefContextProvider>
              </SterBannerProvider>
            </FacebookProvider>
          </GoogleOptimizeProvider>
        </ThemeProvider>
      </React.Fragment>
    );
  }
}

export default Application;
